import Handlebars from '@sparta.global.vendors/handlebars';
import { select } from '@sparta.global.helpers/select-helper/dist/es6.js';
import PaginationDefaultsUtility from '@sparta.global.utilities/pagination-defaults-utility/dist/es6.js';

const ROOT_CSS_CLASS = 'spa-pagination';
const DEFAULTS = PaginationDefaultsUtility.getPaginationDefaults();
const renderFirstOrPrevLink = (config, linkLabel) => {
  const {
    index,
    length,
    size,
    title,
    jcrData
  } = config;
  let disabled = '';
  const previousTitle = `${size} ${jcrData.results} ${title ? `${jcrData.for} ${title}` : ''}`;
  const previousDisable = `${jcrData.results} ${title ? `${jcrData.for} ${title}` : ''}`;
  if (index === 0 && length > 0) {
    disabled = 'disabled';
  }
  const cssModifier = linkLabel === jcrData.first ? 'first' : 'previous';
  let output = `
    <button
      type="button"
      class="${ROOT_CSS_CLASS}-btn ${ROOT_CSS_CLASS}-btn--${cssModifier}"
      data-goto="${linkLabel === jcrData.first ? 0 : index - 1}"
      ${disabled}
    >
      ${linkLabel}
      <span class="ada-hidden">${disabled ? previousDisable : previousTitle}</span>
    </button>
  `;
  output = output.trim();
  return output;
};
const renderNextOrLastLink = (config, linkLabel) => {
  const {
    index,
    length,
    size,
    title,
    jcrData
  } = config;
  let disabled = '';
  const nextTitle = `${size} ${jcrData.results} ${title ? `${jcrData.for} ${title}` : ''}`;
  const nextDisable = `${size} ${jcrData.results} ${title ? `${jcrData.for} ${title}` : ''}`;
  if (index === length - 1 && length > 0) {
    disabled = 'disabled';
  }
  const cssModifier = linkLabel === jcrData.next ? 'next' : 'last';
  let output = `
    <button
      type="button"
      class="${ROOT_CSS_CLASS}-btn ${ROOT_CSS_CLASS}-btn--${cssModifier}"
      data-goto="${linkLabel === jcrData.last ? length - 1 : index + 1}" ${disabled}
    >
      ${linkLabel}
      <span class="ada-hidden">${disabled ? nextTitle : nextDisable}</span>
    </button>
  `;
  output = output.trim();
  return output;
};
const selectBoxRender = (config, dropdown) => {
  const options = [];
  let selectboxText = '';
  if (dropdown === 'page-size') {
    selectboxText = config.jcrData.rowsPerPage;
    for (let optionval = 0; optionval < config.sizeList.length; optionval++) {
      const option = {
        text: config.sizeList[optionval],
        value: config.sizeList[optionval]
      };
      if (config.sizeList[optionval] === config.size) {
        option.selected = 'selected';
      }
      options.push(option);
    }
  } else if (dropdown === 'page-list') {
    selectboxText = config.jcrData.pageType;
    for (let optionval = 1; optionval <= config.length; optionval++) {
      const option = {
        text: optionval,
        value: optionval
      };
      if (optionval === config.index + 1) {
        option.selected = 'selected';
      }
      options.push(option);
    }
  }
  const data = {
    skin: 'condensed',
    label: {
      text: selectboxText
    },
    select: {
      attrs: {
        id: `${config.id}_${dropdown}`,
        name: `${config.id}_${dropdown}`,
        class: `${ROOT_CSS_CLASS}-${dropdown} ${ROOT_CSS_CLASS}-btn--${dropdown}`,
        noEmptyOption: true
      },
      options
    }
  };
  return `${select(data)}`;
};
const renderShowMoreLink = (config, linkLabel) => {
  const {
    index,
    length,
    jcrData,
    showMoreRows,
    noMoreDataPrefix,
    noMoreDataSuffix,
    style
  } = config;
  let disabled = '';
  let adaMessage = '';
  let displayLoadMore = '';
  if (index === length - 1 && length > 0) {
    disabled = 'disabled';
    displayLoadMore = 'none';
  }
  if (index !== 0) {
    adaMessage = `${config.adaText.firstPhrase} ${config.size} ${config.adaText.lastPhrase}`;
  }
  return `
    <span class="ada-hidden" id="${config.id}_${config.domContainer}showMoreMsg" role="alert">
      ${adaMessage}
    </span>
    <button
      type="button"
      class="${ROOT_CSS_CLASS}-btn ${ROOT_CSS_CLASS}-btn--load-more"
      data-goto="${index + 1}" ${disabled}
      style="display: ${displayLoadMore || 'block'}"
    >
      <span aria-hidden="${style && style !== 'progressive' ? 'true' : 'false'}" class="${ROOT_CSS_CLASS}-btn--show-more">
        ${showMoreRows || linkLabel}
      </span>
      <span class="ada-hidden" aria-hidden="${style && style === 'progressive' ? 'true' : 'false'}">
        ${jcrData.showingNext} ${config.size} ${jcrData.of} ${config.title}
      </span>
      <div class="sparta-spinner--placeholder"></div>
    </button>
    <div role="status">
      <span class="ada-hidden">
        ${noMoreDataPrefix || jcrData.noMoreDataPrefix}
      </span>
      <span class="spa-pagination-no-more-data">
      </span>
      <span class="ada-hidden">
        ${noMoreDataSuffix || ''}
      </span>
    </div>
  `;
};
const renderBasicPagination = config => {
  const {
    length,
    jcrData
  } = config;
  let markup = '';
  if (length > 0) {
    // Trim to remove whitespace and get proper spacing for list items.
    let listItems = `
      <li>${renderFirstOrPrevLink(config, jcrData.first)}</li>
      <li>${renderFirstOrPrevLink(config, jcrData.previous)}</li>
      <li>${renderNextOrLastLink(config, jcrData.next)}</li>
      <li>${renderNextOrLastLink(config, jcrData.last)}</li>
    `;
    listItems = listItems.trim();

    // Pagination final output.
    markup = `
      <nav class="${ROOT_CSS_CLASS} ${ROOT_CSS_CLASS}--align-${config.align}">
        <ul>${listItems}</ul>
      </nav>
    `;
    markup = markup.trim();
  }
  return markup;
};
const renderAdvancedPagination = config => {
  const {
    index,
    length,
    size,
    title,
    totalRecords,
    allowPageSizeChange,
    align,
    jcrData
  } = config;
  if (length <= 0) {
    return '';
  }
  const pageFrom = index === 0 && length > 0 ? 1 : index * size + 1;
  let pageTo = (index + 1) * size;
  if (index === length - 1 && length > 0) {
    pageTo = totalRecords;
  }
  const paginationLabel = `
    <li class="${ROOT_CSS_CLASS}-align-frompagedata">
      <span class="${ROOT_CSS_CLASS}-showing-text">${jcrData.showing}</span>
      ${pageFrom}
      <span aria-hidden="true">-</span> <span class="ada-hidden">${jcrData.to}</span>
      ${pageTo} ${jcrData.of} ${totalRecords}
      <span class="ada-hidden">${jcrData.results} ${title ? `${jcrData.for} ${title}` : ''}</span>
    </li>
  `;
  let pageSizeElements = '';
  if (allowPageSizeChange) {
    pageSizeElements = `
      <li class="${ROOT_CSS_CLASS}-align-page-size-list">
        ${selectBoxRender(config, 'page-size')}
      </li>
    `;
  } else {
    pageSizeElements = `<li class="${ROOT_CSS_CLASS}-align-page-size-list"></li>`;
  }
  let pageNavigation = '';
  if (totalRecords > size) {
    pageNavigation = `
    <li
      class="${ROOT_CSS_CLASS}-align-pagecountlist">
      ${selectBoxRender(config, 'page-list')}
    </li>
      <li
        class="${ROOT_CSS_CLASS}-align-previous">
        ${renderFirstOrPrevLink(config, jcrData.previous)}
      </li>
      <li
        class="${ROOT_CSS_CLASS}-align-next">
        ${renderNextOrLastLink(config, jcrData.next)}
      </li>
    `;
  }
  return `
    <nav class="${ROOT_CSS_CLASS} ${ROOT_CSS_CLASS}--align-${align}">
      <ul class="${ROOT_CSS_CLASS}-advanced pagination-left-border-li" data-allowPageSizeChange="${allowPageSizeChange}">
        ${paginationLabel}
        ${pageSizeElements}
        ${pageNavigation}
      </ul>
    </nav>
  `;
};
const renderProgressivePagination = config => {
  const {
    length,
    title,
    jcrData
  } = config;
  let markup = '';
  if (length > 0) {
    markup = `
    <div class="${ROOT_CSS_CLASS} ${ROOT_CSS_CLASS}--align-center">
      ${renderShowMoreLink(config, jcrData.showMoreRows)}
    </div>
    `;
  }
  return markup;
};
const paginationConfiguration = (options, config) => {
  const paginationOptions = {
    ...options
  };
  if (config.title) {
    paginationOptions.title = config.title;
  }
  if (typeof config.data !== 'function') {
    paginationOptions.totalRecords = config.totalRecords;
  }
  const getPageCount = () => {
    let count = 0;
    if (config.totalRecords > 0 && options && options.size > 0) {
      count = Math.ceil(config.totalRecords / options.size);
    } else {
      count = options.totalRecords;
    }
    return count;
  };
  paginationOptions.length = getPageCount();
  return paginationOptions;
};

/**
   * Renders pagination.
   * @param {object} config - Pagination configuration.
   * @returns {string} Generated markup.
   */
function paginationHelper(config) {
  const options = {
    ...DEFAULTS,
    ...config
  };
  if (!config || !options) {
    throw new Error('No pagination configuration provided.');
  }

  // Ensure the pagination controls have a unique ID for ADA.
  options.id = options.id ? `${options.id}_${Math.floor(Math.random() * 1000)}` : `spaPagination${Date.now()}`;
  const pageConfig = paginationConfiguration(options, config);
  let markup = '';
  if (pageConfig) {
    if (pageConfig.style === 'basic') {
      markup = renderBasicPagination(pageConfig);
    } else if (pageConfig.style === 'advanced') {
      markup = renderAdvancedPagination(pageConfig);
    } else if (pageConfig.style === 'progressive') {
      markup = renderProgressivePagination(pageConfig);
    }
  }
  return new Handlebars.SafeString(markup);
}

export { paginationHelper };
